import React, { useLayoutEffect, useRef, useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import StorageNodeActions, { StorageNodeRemoveButton } from './StorageNodeActions';
import StorageNodeIcon from './StorageNodeIcon';
import {
  canHaveChildren,
  formatStorageNodeCountLabel,
  getStorageNodeTypeLabel,
  readSlotCount,
} from '../../utils/storageTree';

/**
 * One place on the storage map. Registered with React Flow as node type
 * `storageNode`; React Flow owns dragging and positioning.
 *
 * `data` carries the storage node, its child count, and add/remove callbacks.
 * The add form lives on the card itself (not in a panel under the map). Inputs
 * use React Flow's `nodrag` so typing does not drag the card.
 *
 * `onSizeChange` reports the card height so the map can push cards below an
 * open add or edit form out of the way. Place item on any node opens a modal
 * on the page so a lot can sit on that place.
 *
 * The buttons and forms come from `StorageNodeActions`, shared with the list
 * view at `/storage?view=list`.
 *
 * @param {{ data: { node: Object, childCount: number, onAdd?: Function, onRemove?: Function, onOpenBox?: Function, onEditBox?: Function, onPlaceItem?: Function, onSizeChange?: Function }, selected: boolean }} props
 */
const StorageMapNode = ({ data, selected }) => {
  const { node, nodes = [], childCount = 0, lotCount = null, onAdd, onRemove, onDeleted, onOpenBox, onEditBox, onPlaceItem, onMove, onRename, onSizeChange } = data;
  const cardRef = useRef(null);
  const openable = canHaveChildren(node);
  const slotCount = readSlotCount(node);
  const [formOpen, setFormOpen] = useState(false);

  // The card grows when the form opens. Tell the map the new height so the
  // place underneath can move down instead of being covered.
  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el || !onSizeChange) return undefined;
    const report = () => onSizeChange(String(node.id), el.offsetHeight, formOpen);
    report();
    if (typeof ResizeObserver !== 'function') return undefined;
    const observer = new ResizeObserver(report);
    observer.observe(el);
    return () => observer.disconnect();
  }, [formOpen, node.id, onSizeChange]);
  const typeLabel = getStorageNodeTypeLabel(node);

  const className = [
    'storage-node',
    openable ? '' : 'storage-node--leaf',
    slotCount ? 'storage-node--slotted' : '',
    formOpen ? 'storage-node--open' : '',
    selected ? 'storage-node--selected' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={className} ref={cardRef}>
      {/* Rooms have no parent, so no incoming handle. */}
      {node?.parent_id != null ? (
        <Handle type="target" position={Position.Top} isConnectable={false} />
      ) : null}

      <div className="storage-node__head">
        <StorageNodeIcon node={node} />
        <span className="storage-node__name">{node?.name}</span>
        {onRemove ? (
          <StorageNodeRemoveButton
            node={node}
            childCount={childCount}
            onRemove={onRemove}
            onDeleted={onDeleted}
          />
        ) : null}
      </div>
      <span className="storage-node__meta">{typeLabel}</span>
      <span className="storage-node__meta">
        {formatStorageNodeCountLabel({ node, childCount, lotCount })}
      </span>

      <StorageNodeActions
        node={node}
        nodes={nodes}
        onAdd={onAdd}
        onOpenBox={onOpenBox}
        onEditBox={onEditBox}
        onMove={onMove}
        onRename={onRename}
        onPlaceItem={onPlaceItem}
        onFormOpenChange={setFormOpen}
      />

      {openable ? (
        <Handle type="source" position={Position.Bottom} isConnectable={false} />
      ) : null}
    </div>
  );
};

export default StorageMapNode;
